import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Briefcase, CalendarDays, Clock, GraduationCap, Map, Route, School } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import FirmLogo from "@/components/app/FirmLogo";
import { Bar, Source } from "@/components/app/featured-cities/CityBlocks";
import { slugTestId } from "@/components/app/featured-cities/cityMeta";
import { BAND_LABEL, firmMeta } from "./firmMeta";

const tile = "rounded-lg border border-stone-200 bg-white p-5 sm:p-6 min-w-0";

function Head({ icon: Icon, children, color }) {
  return <p className="font-data text-xs uppercase tracking-widest text-stone-500 flex items-center gap-2 mb-4"><Icon className="h-4 w-4" style={{ color }} /> {children}</p>;
}

function Empty({ f }) {
  return <p className="text-sm text-stone-500 mb-4">We haven't matched graduate destinations to {f.name} yet. <Link to={f.guide_url} className="underline">See the employer profile</Link>.</p>;
}

export const flagship = (f) => f.offices[0];

export function FirmHero({ f, badge }) {
  const { formatFrom } = useCurrency();
  const m = firmMeta(f);
  const o = flagship(f);
  return (
    <div className="relative overflow-hidden rounded-lg min-h-[320px] lg:min-h-[380px] bg-stone-900 text-white lg:col-span-8 min-w-0" data-testid={`featured-firms-header-${f.slug}`}>
      <img src={m.photo} alt="" className="absolute inset-0 h-full w-full object-cover" data-testid="featured-firms-image" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/15" />
      {badge && <div className="absolute top-4 right-4 z-10">{badge}</div>}
      <div className="relative h-full flex flex-col justify-end p-6 sm:p-8 gap-4">
        <div className="flex items-center gap-3">
          <span className="rounded-md bg-white p-1.5"><FirmLogo slug={f.canonical_slug} name={f.name} sector={f.sector} IconFallback={Briefcase} size={36} /></span>
          <span className="font-data text-xs uppercase tracking-widest px-2 py-1 rounded" style={{ background: m.accent }} data-testid="featured-firms-sector">{f.sector_label}</span>
          {f.deadline?.status === "dated" && <DeadlineChip d={f.deadline} tid="featured-firms-hero-deadline-chip" />}
        </div>
        <h3 className="font-editorial text-4xl sm:text-5xl lg:text-6xl leading-none text-white break-words" data-testid="featured-firms-name">{f.name}</h3>
        <p className="text-sm sm:text-base text-stone-200 max-w-2xl" data-testid="featured-firms-tagline">
          {f.feeder_unis ? `Graduates from ${f.feeder_unis} universities in ${f.feeder_countries} ${f.feeder_countries === 1 ? "country" : "countries"} go on to work here.` : "Graduate destination data for this firm is still being collected."}
        </p>
        {o && (
          <p className="font-data" data-testid="featured-firms-pay-stat">
            <strong className="text-2xl sm:text-3xl">{formatFrom(o.base_annual, o.currency)}</strong>{" "}
            <span className="text-xs text-stone-300">starting base · {o.role} · {o.label}</span>
          </p>
        )}
      </div>
    </div>
  );
}

export function FirmKpis({ f }) {
  const k = [["Feeder universities", f.feeder_unis || "—", "unis"], ["Countries hired from", f.feeder_countries || "—", "countries"],
    ["Degrees that feed in", f.subjects.length || "—", "degrees"], ["Offices with pay data", f.offices.length || "—", "offices"]];
  return (
    <div className="rounded-lg bg-stone-900 text-stone-100 p-6 lg:col-span-4 grid grid-cols-2 gap-5 content-between min-w-0" data-testid="featured-firms-kpi-tile">
      {k.map(([n, v, id]) => (
        <div key={id} className="min-w-0">
          <p className="font-data text-3xl sm:text-4xl font-semibold tracking-tight" data-testid={`featured-firms-kpi-${id}`}>{v}</p>
          <p className="font-data text-[11px] uppercase tracking-widest text-stone-400 mt-1">{n}</p>
        </div>
      ))}
      <p className="col-span-2 font-data text-[11px] text-stone-500 italic">Source: graduate destinations on WUA course pages · curated salary bands</p>
    </div>
  );
}

export function FeedersTile({ f, accent }) {
  return (
    <div className={`${tile} lg:col-span-4`} data-testid="featured-firms-feeders-block">
      <Head icon={School} color={accent}>Top feeder universities</Head>
      {f.feeders.length ? (
        <ol className="space-y-3 mb-4">
          {f.feeders.map((x, i) => (
            <li key={x.id} className="flex items-start gap-2 text-sm" data-testid={`featured-firms-feeder-${x.id}`}>
              <span className="font-data text-xs w-5 text-stone-500 pt-0.5">#{i + 1}</span>
              <span className="flex-1 min-w-0">
                <Link to={`/uni/${x.id}`} className="block truncate text-stone-900 hover:underline">{x.name}</Link>
                <span className="text-xs text-stone-500">{x.country}{x.subject ? ` · ${x.subject}` : ""}</span>
              </span>
              <span className="text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap" style={{ borderColor: accent, color: accent }}
                title={x.report ? `Named in ${x.report}` : undefined}>
                {x.pct ? `${x.pct}% of cohort` : x.report ? (x.report.includes("master's") ? "Master's report" : "Employment report") : BAND_LABEL[x.band]}
              </span>
            </li>
          ))}
        </ol>
      ) : <Empty f={f} />}
      <Source>{f.feeders.some((x) => x.report) ? `graduate destinations + ${[...new Set(f.feeders.filter((x) => x.report).map((x) => x.report))].slice(0, 2).join("; ")}` : f.editorial ? "graduate destinations (partly editorial patterns)" : "graduate destinations (HESA LEO, national surveys)"}</Source>
    </div>
  );
}

export function SalaryTile({ f, accent }) {
  const { formatFrom, convertFrom } = useCurrency();
  const max = Math.max(...f.offices.map((o) => convertFrom(o.total_max_annual || o.base_annual, o.currency)), 1);
  return (
    <div className={`${tile} lg:col-span-4`} data-testid="featured-firms-salary-block">
      <Head icon={Map} color={accent}>Starting salary by office</Head>
      <div className="space-y-4 mb-4">
        {f.offices.map((o) => (
          <div key={o.label} data-testid={`featured-firms-office-${slugTestId(o.label)}`}>
            <p className="flex justify-between gap-2 text-sm mb-1"><span className="text-stone-800 min-w-0 truncate">{o.label} <span className="text-xs text-stone-500">· {o.role}</span></span><span className="font-data text-xs whitespace-nowrap">{formatFrom(o.base_annual, o.currency)}</span></p>
            <Bar value={convertFrom(o.base_annual, o.currency)} max={max} color={accent} />
            {o.total_max_annual && <p className="text-[11px] text-stone-500 mt-1">up to {formatFrom(o.total_max_annual, o.currency)} with bonus</p>}
          </div>
        ))}
      </div>
      <Source>{f.sources.join(" · ") || "curated salary bands"}</Source>
    </div>
  );
}

export function DegreesTile({ f, accent }) {
  const max = Math.max(...f.subjects.map((s) => s.count), 1);
  return (
    <div className={`${tile} lg:col-span-4`} data-testid="featured-firms-degrees-block">
      <Head icon={GraduationCap} color={accent}>Degrees that get you in</Head>
      {f.subjects.length ? (
        <div className="space-y-3 mb-4">
          {f.subjects.map((s) => (
            <div key={s.subject} data-testid={`featured-firms-degree-${slugTestId(s.subject)}`}>
              <p className="flex justify-between text-sm mb-1"><span className="text-stone-800">{s.subject}</span><span className="font-data text-xs text-stone-500">{s.count} course{s.count === 1 ? "" : "s"}</span></p>
              <Bar value={s.count} max={max} color={accent} />
            </div>
          ))}
        </div>
      ) : <Empty f={f} />}
      <Source>courses whose graduates list {f.name} as a top employer</Source>
    </div>
  );
}

export function RouteTile({ f, accent }) {
  const p = f.primer;
  return (
    <div className="rounded-lg border border-stone-200 bg-[#FBF8F3] p-5 sm:p-6 lg:col-span-8 min-w-0" data-testid="featured-firms-route-block">
      <Head icon={Route} color={accent}>Typical route · {p.label}</Head>
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="featured-firms-route-steps">
        {p.typical_route.map((step, i) => (
          <span key={step} className="inline-flex items-center gap-2">
            <span className="rounded-full px-3 py-1.5 text-sm text-white" style={{ background: accent }}>{step}</span>
            {i < p.typical_route.length - 1 && <ArrowRight className="h-4 w-4 text-stone-400" />}
          </span>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-3 text-sm">
        <div data-testid="featured-firms-academic-bar">
          <p className="font-data text-[11px] uppercase tracking-widest text-stone-500 mb-1">Academic bar</p>
          <p className="font-data text-lg font-semibold text-stone-900">{p.bar}</p>
          <p className="text-stone-700">{p.academic_bar}</p>
        </div>
        <div><p className="font-data text-[11px] uppercase tracking-widest text-stone-500 mb-1">Act early</p><p className="text-stone-700">{p.early_action}</p></div>
        <div><p className="font-data text-[11px] uppercase tracking-widest text-stone-500 mb-1">What stands out</p><p className="text-stone-700">{p.hard_signals}</p></div>
      </div>
      <p className="font-data text-[11px] text-stone-400 italic mt-4">Source: WUA editorial guidance by sector</p>
    </div>
  );
}

export function EventsTile({ f, accent }) {
  if (!f.events.length) return null;
  return (
    <div className={`${tile} lg:col-span-12`} data-testid="featured-firms-events-block">
      <Head icon={CalendarDays} color={accent}>Upcoming from {f.name}</Head>
      <ul className="grid gap-3 md:grid-cols-3">
        {f.events.map((e) => (
          <li key={e.id} className="rounded border border-stone-200 p-3" data-testid={`featured-firms-event-${e.id}`}>
            <p className="font-data text-xs text-stone-500">{new Date(e.starts_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })} · {e.is_virtual ? "Online" : e.location}</p>
            <p className="text-sm font-medium text-stone-900 mt-1">{e.title}</p>
            {e.rsvp_url && <a href={e.rsvp_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium mt-2 hover:underline" style={{ color: accent }}>Register <ArrowUpRight className="h-3.5 w-3.5" /></a>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DeadlineChip({ d, onDark = false, tid = "featured-firms-deadline-chip" }) {
  if (!d) return null;
  const days = d.status === "dated" ? Math.ceil((new Date(d.deadline_at) - Date.now()) / 86400000) : null;
  if (days !== null && days < 0) return null;
  const urgent = days !== null && days <= 14;
  const text = days === null ? (d.status === "rolling" ? "Applications open · rolling, apply early" : "Dates not published yet")
    : days === 0 ? "Closes today" : days === 1 ? "Closes tomorrow" : `Closes in ${days} days`;
  const tone = urgent ? "bg-rose-600 text-white" : days !== null ? "bg-amber-400 text-stone-900" : onDark ? "bg-white/15 text-white" : "bg-stone-100 text-stone-700";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${tone}`} data-testid={tid}>
      <Clock className="h-3.5 w-3.5" /> {text}
    </span>
  );
}

function DeadlineBlock({ d: raw }) {
  if (!raw) return null;
  const d = raw.status === "dated" && new Date(raw.deadline_at) < Date.now() ? { status: "unpublished", source_url: raw.source_url } : raw;
  const checked = d.verified_at ? new Date(d.verified_at).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : null;
  return (
    <div className="rounded-md bg-white/10 p-3 space-y-1.5" data-testid="featured-firms-deadline-block">
      <DeadlineChip d={d} onDark />
      {d.title && <p className="text-sm text-white/90">{d.title}{d.status === "dated" && ` · closes ${new Date(d.deadline_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`}</p>}
      {d.status === "unpublished" && <p className="text-sm text-white/80">No closing date is published yet. Check the careers page.</p>}
      {d.source_url && (
        <a href={d.apply_url || d.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-white/80 hover:text-white underline" data-testid="featured-firms-deadline-source">
          {d.status === "unpublished" ? "Careers page" : `Source: careers page${checked ? ` · checked ${checked}` : ""}`} <ArrowUpRight className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}

export function FirmCta({ f, share }) {
  return (
    <div className="rounded-lg bg-[#002147] text-white p-6 lg:col-span-4 flex flex-col justify-between gap-6 min-w-0" data-testid="featured-firms-cta-tile">
      <p className="font-editorial text-2xl sm:text-3xl leading-tight">Could you get into {f.name}?</p>
      <DeadlineBlock d={f.deadline} />
      <div className="flex flex-col gap-2">
        <Link to={f.guide_url} className="inline-flex items-center justify-between rounded-full bg-white text-[#002147] px-5 py-3 font-medium transition-transform hover:-translate-y-0.5" data-testid="featured-firms-guide-button">
          How to get into {f.name} <ArrowUpRight className="h-4 w-4 shrink-0" />
        </Link>
        {share}
      </div>
    </div>
  );
}
